import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import {
  MessageSquareQuote, ChevronRight, Loader2, ShieldAlert,
  Info, HelpCircle,
} from "lucide-react";

/**
 * AccountantTipsCard — Module 7 helper.
 *
 * Renders 3-6 short, question-shaped tips the tradie can raise with
 * their accountant BEFORE lodgement. The tips are data-driven (missing
 * ABNs, unassigned jobs, WFH hours, PSI concentration, etc.) so a
 * quiet ledger doesn't waste screen real-estate with irrelevant
 * prompts. Tips are conversation starters only — NOT advice.
 */
const SEV_STYLE = {
  warn: {
    ring: "border-[#FBBF24]/30 bg-[#FBBF24]/[0.06]",
    text: "text-[#FBBF24]",
    Icon: ShieldAlert,
  },
  info: {
    ring: "border-white/10 bg-white/[0.02]",
    text: "text-[#FF9500]",
    Icon: HelpCircle,
  },
};

export default function AccountantTipsCard({ fy, testid = "accountant-tips-card" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const params = useMemo(() => (fy ? `?fy=${fy}` : ""), [fy]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: res } = await api.get(`/tax/accountant-tips${params}`);
        if (!cancelled) setData(res);
      } catch (err) {
        devLog.warn("accountant tips fetch failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  if (loading) {
    return (
      <div
        className="bi-surface p-3 flex items-center gap-2 text-[11px] text-white/40"
        data-testid={`${testid}-loading`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing tips…
      </div>
    );
  }

  if (!data || !data.tips?.length) {
    return null;
  }

  const warnCount = data.tips.filter((t) => t.severity === "warn").length;

  return (
    <div
      data-testid={testid}
      className="bi-surface p-3 border border-[#60A5FA]/25 bg-[#60A5FA]/[0.04]"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center gap-2 text-left"
      >
        <MessageSquareQuote className="w-4 h-4 text-[#60A5FA] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold uppercase tracking-wider text-[11px] text-[#60A5FA]">
            {data.count} question{data.count === 1 ? "" : "s"} to ask your accountant
          </div>
          <div className="text-[10px] text-white/50 truncate mt-0.5">
            {warnCount > 0
              ? `${warnCount} worth flagging first · ${data.tips[0].category}`
              : "Tap to reveal — smart prompts based on your ledger"}
          </div>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>

      {open && (
        <div className="mt-3 space-y-2" data-testid={`${testid}-list`}>
          {data.tips.map((t, i) => {
            const s = SEV_STYLE[t.severity] || SEV_STYLE.info;
            const Icon = s.Icon;
            return (
              <div
                key={t.id}
                data-testid={`${testid}-row-${t.id}`}
                className={`rounded-lg p-2.5 border ${s.ring} space-y-1`}
              >
                <div className="flex items-start gap-2">
                  <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${s.text}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-[9px] uppercase tracking-[0.18em] font-heading ${s.text} mb-0.5`}>
                      {t.category}
                    </div>
                    <div className="text-[12px] text-white/90 leading-snug">
                      {t.question}
                    </div>
                  </div>
                </div>
                {t.why && (
                  <div className="text-[10px] text-white/50 leading-snug pl-6">
                    {t.why}
                  </div>
                )}
              </div>
            );
          })}
          <div
            className="mt-2 pt-2 border-t border-white/5 flex items-start gap-1.5 text-[10px] text-white/40 leading-snug"
            data-testid={`${testid}-disclaimer`}
          >
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{data.disclaimer}</span>
          </div>
        </div>
      )}
    </div>
  );
}
